import type { ChaocEvent, EventCategory } from '../contracts/events';
import { appEnv, isProductionLike } from '../app/env';
import { loadMapLibre } from './maplibreLoader';
import {
  hexCellCenter,
  hexGridAnchorZoom,
  hexPixelRadiusForRes,
  hexRes,
  lngLatToWorld,
  worldScale,
  worldToHexCell,
  worldToLngLat,
} from './mercatorHexGrid';

type MapLibreModule = typeof import('maplibre-gl');
type MapLike = InstanceType<MapLibreModule['Map']>;

interface HexFeatureProperties {
  hid: string;
  count: number;
  avgAct: number;
  avgVol: number;
  isActive: boolean;
  isSelected: boolean;
  hasUserFlag: boolean;
}

interface HexFeature {
  type: 'Feature';
  properties: HexFeatureProperties;
  geometry: { type: 'Polygon'; coordinates: [number, number][][] };
}

interface HexFeatureCollection {
  type: 'FeatureCollection';
  features: HexFeature[];
}

interface HexRenderFrame {
  res: number;
  scale: number;
  size: number;
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
  qMin: number;
  qMax: number;
  rMin: number;
  rMax: number;
  unwrapX: (x: number) => number;
}

export interface ChaocMapCallbacks {
  onHexSelected: (hexId: string, events: ChaocEvent[]) => void;
  onResolutionChanged: (label: string) => void;
  onReadyChanged: (ready: boolean) => void;
}

export interface MapViewState {
  events: ChaocEvent[];
  filter: EventCategory;
  confidenceThreshold: number;
  selectedHexId: string | null;
}

export class ChaocMap {
  private maplibre: MapLibreModule | null = null;
  private map: MapLike | null = null;
  private ready = false;
  private currentZoom = 2.2;
  private viewState: MapViewState = {
    events: [],
    filter: 'all',
    confidenceThreshold: 0,
    selectedHexId: null,
  };

  constructor(
    private readonly container: HTMLElement,
    private readonly callbacks: ChaocMapCallbacks,
  ) {}

  async init(): Promise<void> {
    this.maplibre = await loadMapLibre();
    const style = this.resolveStyleUrl();
    if (!style) {
      this.callbacks.onReadyChanged(false);
      return;
    }

    this.map = new this.maplibre.Map({
      container: this.container,
      style,
      center: [10, 25],
      zoom: this.currentZoom,
      minZoom: 1,
      maxZoom: 16,
      attributionControl: false,
    });
    this.map.addControl(new this.maplibre.AttributionControl({ compact: true }), 'bottom-right');

    this.map.on('load', () => {
      this.ready = true;
      this.currentZoom = this.map?.getZoom() || this.currentZoom;
      this.callbacks.onReadyChanged(true);
      this.callbacks.onResolutionChanged(`res-${hexRes(this.currentZoom)}`);
      this.addHexLayer();
    });

    this.map.on('zoom', () => {
      this.currentZoom = this.map?.getZoom() || this.currentZoom;
      this.callbacks.onResolutionChanged(`res-${hexRes(this.currentZoom)}`);
      this.refreshHex();
    });
    this.map.on('moveend', () => this.refreshHex());
    this.map.on('resize', () => this.refreshHex());
  }

  destroy(): void {
    this.map?.remove();
    this.map = null;
    this.ready = false;
  }

  setViewState(viewState: MapViewState): void {
    this.viewState = viewState;
    this.refreshHex();
  }

  flyTo(lng: number, lat: number, zoom: number): void {
    const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    this.map?.flyTo({ center: [lng, lat], zoom, duration: reducedMotion ? 0 : 800, essential: true });
  }

  zoomIn(): void {
    this.map?.zoomIn();
  }

  zoomOut(): void {
    this.map?.zoomOut();
  }

  recenter(): void {
    this.flyTo(10, 25, 2.2);
  }

  private resolveStyleUrl(): string | null {
    if (appEnv.maptilerKey) {
      return `https://api.maptiler.com/maps/dataviz-dark/style.json?key=${encodeURIComponent(appEnv.maptilerKey)}`;
    }
    if (!isProductionLike()) {
      return 'https://demotiles.maplibre.org/style.json';
    }
    return null;
  }

  private addHexLayer(): void {
    if (!this.map || !this.ready) return;
    if (this.map.getSource('hexes')) return;
    this.map.addSource('hexes', { type: 'geojson', data: this.buildHexGeoJSON() });

    this.map.addLayer({
      id: 'hex-grid',
      type: 'line',
      source: 'hexes',
      paint: {
        'line-color': ['case', ['==', ['get', 'isActive'], true], '#2A1A14', '#182536'],
        'line-width': ['case', ['==', ['get', 'isActive'], true], 0.7, 0.5],
        'line-opacity': ['case', ['==', ['get', 'isActive'], true], 0.65, 0.38],
      },
    });

    this.map.addLayer({
      id: 'hex-fill',
      type: 'fill',
      source: 'hexes',
      paint: {
        'fill-color': [
          'interpolate',
          ['linear'],
          ['to-number', ['get', 'avgAct'], 0],
          0,
          '#071018',
          0.1,
          '#160A06',
          0.35,
          '#5A2010',
          0.6,
          '#9B341C',
          0.8,
          '#C0492B',
          1,
          '#D45534',
        ],
        'fill-opacity': [
          'case',
          ['==', ['get', 'isActive'], false],
          0,
          ['case', ['==', ['get', 'isSelected'], true], 0.94, 0.75],
        ],
      },
    });

    this.map.addLayer({
      id: 'hex-active-border',
      type: 'line',
      source: 'hexes',
      paint: {
        'line-color': ['case', ['==', ['get', 'isSelected'], true], '#C0492B', '#5A2A18'],
        'line-width': ['case', ['==', ['get', 'isSelected'], true], 1.8, 0.75],
        'line-opacity': ['case', ['==', ['get', 'isActive'], true], 0.9, 0],
      },
    });

    this.map.addLayer({
      id: 'hex-vol-ring',
      type: 'line',
      source: 'hexes',
      paint: {
        'line-color': '#8B4FC8',
        'line-width': ['interpolate', ['linear'], ['to-number', ['get', 'avgVol'], 0], 0.5, 0.8, 1, 2],
        'line-opacity': ['interpolate', ['linear'], ['to-number', ['get', 'avgVol'], 0], 0, 0, 0.45, 0, 0.5, 0.3, 0.75, 0.7, 1, 0.95],
        'line-gap-width': 3,
      },
    });

    this.map.addLayer({
      id: 'hex-user-flag',
      type: 'line',
      source: 'hexes',
      paint: {
        'line-color': '#2D6FBF',
        'line-width': 1.5,
        'line-opacity': ['case', ['==', ['get', 'hasUserFlag'], true], 0.85, 0],
        'line-gap-width': 5,
      },
    });

    this.map.on('click', 'hex-fill', (event) => {
      const feature = event.features?.[0];
      const props = feature?.properties as Partial<HexFeatureProperties> | undefined;
      if (!props?.isActive || !props.hid) return;
      const frame = this.computeHexFrame();
      const events = frame ? this.visibleEvents().filter((item) => this.eventHexCell(item, frame).id === props.hid) : [];
      this.callbacks.onHexSelected(props.hid, events);
    });

    this.map.on('mouseenter', 'hex-fill', () => {
      if (this.map) this.map.getCanvas().style.cursor = 'crosshair';
    });
    this.map.on('mouseleave', 'hex-fill', () => {
      if (this.map) this.map.getCanvas().style.cursor = '';
    });
  }

  private refreshHex(): void {
    if (!this.map || !this.ready) return;
    const source = this.map.getSource('hexes') as { setData?: (data: HexFeatureCollection) => void } | undefined;
    source?.setData?.(this.buildHexGeoJSON());
  }

  private visibleEvents(): ChaocEvent[] {
    const threshold = this.viewState.confidenceThreshold / 100;
    return this.viewState.events.filter((event) => {
      if (event.confidence < threshold) return false;
      return this.viewState.filter === 'all' || event.category === this.viewState.filter;
    });
  }

  private buildHexGeoJSON(): HexFeatureCollection {
    const frame = this.computeHexFrame();
    if (!frame) return { type: 'FeatureCollection', features: [] };

    const active = new Map<string, { events: ChaocEvent[]; totalAct: number; totalVol: number; hasUserFlag: boolean }>();
    for (const event of this.visibleEvents()) {
      const cell = this.eventHexCell(event, frame);
      const current = active.get(cell.id) || { events: [], totalAct: 0, totalVol: 0, hasUserFlag: false };
      current.events.push(event);
      current.totalAct += event.activity;
      current.totalVol += event.volatility;
      current.hasUserFlag ||= !!event.userFlag;
      active.set(cell.id, current);
    }

    const features: HexFeature[] = [];
    for (let q = frame.qMin; q <= frame.qMax; q += 1) {
      for (let r = frame.rMin; r <= frame.rMax; r += 1) {
        const center = hexCellCenter(q, r, frame.res);
        if (
          center.x < frame.minX - frame.size * 2 ||
          center.x > frame.maxX + frame.size * 2 ||
          center.y < frame.minY - frame.size * 2 ||
          center.y > frame.maxY + frame.size * 2
        ) {
          continue;
        }
        const hid = `WM${frame.res}:${q}:${r}`;
        const data = active.get(hid);
        const count = data?.events.length || 0;
        const coords: [number, number][] = [];
        for (let i = 0; i < 6; i += 1) {
          const angle = (Math.PI / 180) * (60 * i);
          coords.push(worldToLngLat(center.x + frame.size * Math.cos(angle), center.y + frame.size * Math.sin(angle), frame.scale));
        }
        coords.push(coords[0]);
        features.push({
          type: 'Feature',
          properties: {
            hid,
            count,
            avgAct: count && data ? data.totalAct / count : 0,
            avgVol: count && data ? data.totalVol / count : 0,
            isActive: count > 0,
            isSelected: hid === this.viewState.selectedHexId,
            hasUserFlag: !!data?.hasUserFlag,
          },
          geometry: { type: 'Polygon', coordinates: [coords] },
        });
      }
    }
    return { type: 'FeatureCollection', features };
  }

  private computeHexFrame(): HexRenderFrame | null {
    if (!this.map) return null;

    const res = hexRes(this.currentZoom);
    const scale = worldScale(hexGridAnchorZoom(res));
    const size = hexPixelRadiusForRes(res);
    const canvas = this.map.getCanvas();
    const width = canvas.clientWidth || canvas.width || 1000;
    const height = canvas.clientHeight || canvas.height || 600;
    const pad = size * 2;

    const corners = [
      [-pad, -pad],
      [width + pad, -pad],
      [width + pad, height + pad],
      [-pad, height + pad],
    ]
      .map(([x, y]) => this.map?.unproject([x, y]) || { lng: 0, lat: 0 })
      .map((ll) => lngLatToWorld(ll.lng, ll.lat, scale));

    const anchorX = corners[0]?.x ?? 0;
    const unwrapX = (x: number): number => {
      let value = x;
      while (value - anchorX > scale / 2) value -= scale;
      while (anchorX - value > scale / 2) value += scale;
      return value;
    };
    const unwrappedCorners = corners.map((corner) => ({ ...corner, x: unwrapX(corner.x) }));

    const minX = Math.min(...unwrappedCorners.map((corner) => corner.x));
    const maxX = Math.max(...unwrappedCorners.map((corner) => corner.x));
    const minY = Math.min(...corners.map((corner) => corner.y));
    const maxY = Math.max(...corners.map((corner) => corner.y));
    const boundsCells = [
      worldToHexCell(minX, minY, res),
      worldToHexCell(maxX, minY, res),
      worldToHexCell(maxX, maxY, res),
      worldToHexCell(minX, maxY, res),
    ];

    return {
      res,
      scale,
      size,
      minX,
      maxX,
      minY,
      maxY,
      qMin: Math.min(...boundsCells.map((cell) => cell.q)) - 2,
      qMax: Math.max(...boundsCells.map((cell) => cell.q)) + 2,
      rMin: Math.min(...boundsCells.map((cell) => cell.r)) - 2,
      rMax: Math.max(...boundsCells.map((cell) => cell.r)) + 2,
      unwrapX,
    };
  }

  private eventHexCell(event: ChaocEvent, frame: HexRenderFrame) {
    const world = lngLatToWorld(event.lng, event.lat, frame.scale);
    return worldToHexCell(frame.unwrapX(world.x), world.y, frame.res);
  }
}
